// Mirror of worker/src/types.ts — kept in sync by convention.

export type Game = "el_la" | "conj" | "vocab";
export type ProfileId = "matt" | "malcolm" | "pavel" | "batu";
export type PairId = "matt-malcolm" | "pavel-batu";
export type SupportLevel = "guided" | "standard" | "challenge";
export type Tense =
  | "pres"
  | "perf"
  | "pret"
  | "impf"
  | "fut"
  | "pres_prog"
  | "past_prog";
export type Person = "1s" | "2s" | "3s" | "1p" | "3p";

export interface ElLaItem {
  kind: "el_la";
  id: string;
  item_kind: "noun";
  item_id: number;
  lemma: string;
  english: string;
  correct_article: "el" | "la";
  note: string | null;
  difficulty: 1 | 2 | 3;
  srs: boolean;
}

export interface ConjItem {
  kind: "conj";
  id: string;
  item_kind: "verb";
  item_id: number;
  infinitive: string;
  english: string;
  tense: Tense;
  person: Person;
  correct_form: string;
  sentence: string;
  options: string[];
  timeline: ConjTimelineEntry[];
  gerund: string;
  difficulty: 1 | 2 | 3;
  srs: boolean;
}

export interface ConjTimelineEntry {
  tense: Tense;
  label: string;
  cue: string;
  form: string;
}

export interface VocabItem {
  kind: "vocab";
  id: string;
  item_kind: "vocab";
  item_id: number;
  direction: "en2es" | "es2en";
  prompt: string;
  answers: string[];
  category: string;
  note: string | null;
  dialect: string | null;
  difficulty: 1 | 2 | 3;
  srs: boolean;
}

export type DailyItem = ElLaItem | ConjItem | VocabItem;

export interface DailySession {
  session_id: string;
  game: Game;
  profile_id: ProfileId;
  date_key: string;
  items: DailyItem[];
  correct: number;
  total: number;
  best_streak: number;
  completed_at: number | null;
  answered_count: number;
  support_level: SupportLevel;
}

export interface AnswerResult {
  correct: boolean;
  correct_answer: string;
  user_answer: string;
  explanation: string | null;
  ai_adjudicated: boolean;
  streak: number;
  session_correct: number;
  session_total: number;
}

export interface HomeStats {
  xp: number;
  level: number;
  level_title: string;
  xp_to_next: number;
  lifetime_correct: number;
  lifetime_total: number;
  accuracy: number;
  longest_el_la: number;
  longest_conj: number;
  longest_vocab: number;
  day_streak: number;
  last_played_date: string | null;
  h2h_wins: number;
  achievements: { code: string; unlocked_at: number }[];
}

export interface HomeResponse {
  profiles: Record<ProfileId, HomeStats>;
  duos: Record<PairId, {
    duo_streak: number;
    longest_duo_streak: number;
    last_duo_date: string | null;
  }>;
}

export interface CompletionReward {
  ok: true;
  already?: boolean;
  xp_gain: number;
  unlocked: string[];
}

export interface RelayItem {
  item_index: number;
  item_id: number;
  prompt_en: string;
  answers_es: string[];
  category: string;
  options: string[];
}

export interface RelayAttempt {
  item_index: number;
  attempt: 0 | 1;
  correct: boolean;
}

export interface RelayScore {
  profile_id: ProfileId;
  correct: number;
  target: number;
  recovered: number;
}

export interface RelayWeeklyWin {
  profile_id: ProfileId;
  wins: number;
}

export interface RelaySession {
  pair_id: PairId;
  date_key: string;
  theme_key: string;
  theme_title: string;
  theme_emoji: string;
  items: RelayItem[];
  profile_id: ProfileId;
  opponent_id: ProfileId;
  support_level: SupportLevel;
  target: number;
  attempts: RelayAttempt[];
  completed: boolean;
  correct: number;
  recovered: number;
  opponent_completed: boolean;
  reveal: null | {
    scores: [RelayScore, RelayScore];
    winner: ProfileId | "tie";
    duo_streak: number;
    weekly: { wins: [RelayWeeklyWin, RelayWeeklyWin]; ties: number };
  };
}

export interface TodayProgress {
  correct: number;
  total: number;
  best_streak: number;
  completed: boolean;
}

export interface StudyCard {
  kind: "el_la" | "vocab" | "conj";
  item_key: string;
  game: Game;
  item_id: number;
  prompt: string;
  answer: string;
  note: string | null;
  meta: string | null;
  english: string | null;
  times_wrong: number;
  updated_at: number;
  timeline?: ConjTimelineEntry[];
  gerund?: string;
}

export const TENSE_LABEL: Record<Tense, string> = {
  pres: "Presente",
  perf: "Pretérito perfecto",
  pret: "Pretérito indefinido",
  impf: "Imperfecto",
  fut: "Futuro",
  pres_prog: "Presente progresivo",
  past_prog: "Pasado Progresivo",
};

export const PERSON_LABEL: Record<Person, string> = {
  "1s": "yo",
  "2s": "tú",
  "3s": "él/ella",
  "1p": "nosotros",
  "3p": "ellos/ellas",
};

export const GAME_LABEL: Record<Game, { title: string; subtitle: string; emoji: string }> = {
  el_la: { title: "El o La", subtitle: "Artículos", emoji: "📘" },
  conj: { title: "Conjuga", subtitle: "Verbos", emoji: "🎯" },
  vocab: { title: "Vocabulario", subtitle: "Palabras", emoji: "💬" },
};

export const GAME_TOTAL: Record<Game, number> = {
  el_la: 15,
  conj: 10,
  vocab: 15,
};
